import { Component, OnInit, OnDestroy, HostListener, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { get } from 'lodash'; // for copy data cell value
import {
  ColDef,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  PaginationChangedEvent,
} from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { AgGridModule } from 'ag-grid-angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { GroupDialogComponent, GroupDialogData } from '../../Dialogs/group-dialog/group-dialog.component';
import { ConfirmDeleteGroupComponent } from '../../Dialogs/confirm-delete-group/confirm-delete-group.component';
import { GroupActionCellRendererComponent } from './group-action-cell-renderer.component';
import {
  AG_GRID_DEFAULT_PAGE_SIZE,
  AG_GRID_PAGE_SIZE_OPTIONS,
  AG_GRID_DEFAULT_ROW_HEIGHT_PX,
  AG_GRID_EMPTY_BODY_ROW_PX,
  AG_GRID_HEADER_AND_PAGING_PX,
} from '../../shared/ag-grid/ag-grid-pagination.util';
import { GroupService } from '../../services/group-service/group.service';
import { CustomerService } from '../../services/customer-service/customer.service';
import { DeviceService } from '../../services/device-service/device.service';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { RbacPermissions } from '../../services/rbac/rbac-permissions';
import { RbacService } from '../../services/rbac/rbac.service';
import { AgGridCopyCellTooltipDirective } from '../../shared/ag-grid/ag-grid-copy-cell-tooltip.directive';
import { CanComponentDeactivate } from '../../guards/unsaved-changes.guard';
import { UnsavedChangesTrackerService } from '../../services/unsaved-changes/unsaved-changes-tracker.service';
import { UnsavedChangesHostHelper } from '../../services/unsaved-changes/unsaved-changes-host.helper';
import {
  createUtcDateFilterParams,
  formatUtcDateTimeCell,
  mapAgGridFilterToApi,
  withDateColumnLayout,
} from '../../shared/ag-grid/ag-grid-date-column.util';
import {
  CUSTOMER_DROPDOWN_SELECT_LABEL,
  GROUP_DROPDOWN_SELECT_CUSTOMER_FIRST,
  getGroupDropdownEmptyLabel,
  hasConcreteCustomerSelected,
  isGroupDropdownDisabled,
} from '../../services/group-dropdown.util';

/** Wait after last keystroke before applying text filters / calling search. */
const FILTER_DEBOUNCE_MS = 400;

const TEXT_FILTER_PARAMS = {
  filterOptions: ['contains'],
  defaultOption: 'contains' as const,
  trimInput: true,
  maxNumConditions: 1,
  suppressFilterButton: true,
  debounceMs: FILTER_DEBOUNCE_MS,
};

/**
 * Custom floating filter for Group Type column (AG Grid v33, Community).
 * Renders a native <select> with All / Public / Private options.
 */
class GroupTypeFloatingFilter {
  private eGui!: HTMLSelectElement;
  private api!: any;
  private column!: any;

  init(params: any): void {
    this.api    = params.api;
    this.column = params.column;

    this.eGui = document.createElement('select');
    this.eGui.style.cssText =
      'width:100%;height:28px;border:1px solid #babfc7;border-radius:3px;' +
      'font-size:12px;padding:0 4px;background:#fff;color:#333;cursor:pointer;';

    [
      { label: 'All',     value: '' },
      { label: 'Public',  value: 'Public' },
      { label: 'Private', value: 'Private' },
    ].forEach(({ label, value }) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = label;
      this.eGui.appendChild(opt);
    });

    this.eGui.addEventListener('change', () => {
      const val = this.eGui.value;
      const colId = this.column.getColId();
      const filterInstance = this.api.getColumnFilterInstance(colId);

      Promise.resolve(filterInstance).then((instance: any) => {
        if (!instance) return;
        instance.setModel(val === '' ? null : { filterType: 'text', type: 'contains', filter: val });
        this.api.onFilterChanged();
      });
    });

    // Scope style fix only to Group Type floating filter body.
    queueMicrotask(() => {
      const body = this.eGui.closest('.ag-floating-filter-body') as HTMLElement | null;
      if (!body) return;
      body.style.position = 'relative';
      body.style.top = '6px';
    });
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  onParentModelChanged(parentModel: any): void {
    this.eGui.value = parentModel?.filter ?? '';
  }

  destroy(): void {}
}

const SESSION_SORT_KEY = 'groupManagementSortingState';

@Component({
  selector: 'app-group-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    AgGridAngular,
    AgGridModule,
    MatIconModule,
    HasPermissionDirective,
    AgGridCopyCellTooltipDirective,
  ],
  templateUrl: './group-management.component.html',
  styleUrl: './group-management.component.scss',
})
export class GroupManagementComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  readonly RbacPermissions = RbacPermissions;

  private readonly rbacService = inject(RbacService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly unsavedHost = new UnsavedChangesHostHelper(
    inject(UnsavedChangesTrackerService),
    inject(MatDialog),
    inject(Router)
  );
  private fullColumnDefs: ColDef[] | null = null;

  rowData: any[] = [];
  isLoading = false;
  paginationPageSize = AG_GRID_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = AG_GRID_PAGE_SIZE_OPTIONS;
  currentPage: number = 0;
  gridApi: any;
  sortFlag = false;
  totalElements = 0;
  /** Host height from loaded rows — page scrolls, not the grid. */
  gridHostHeightPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;

  private searchSub: Subscription | null = null;
  private syncingHeaderColumnFilters = false;
  customerList: any[] = [];
  selectedCustomer = 'All';
  groups: any[] = [];
  selectedGroup = '';
  groupsLoaded = true;
  groupDropdownEmptyLabel = GROUP_DROPDOWN_SELECT_CUSTOMER_FIRST;
  isGroupDropdownDisabled = true;
  readonly customerDropdownSelectLabel = CUSTOMER_DROPDOWN_SELECT_LABEL;

  private refreshGroupDropdownState(): void {
    const hasCustomer = hasConcreteCustomerSelected(this.selectedCustomer);
    const count = this.groups?.length ?? 0;
    this.groupDropdownEmptyLabel = getGroupDropdownEmptyLabel(
      hasCustomer,
      count,
      this.groupsLoaded
    );
    this.isGroupDropdownDisabled = isGroupDropdownDisabled(
      hasCustomer,
      count,
      this.groupsLoaded
    );
  }

  private applyLoadedGroups(groups: any[] | null | undefined): void {
    this.groups = groups || [];
    this.groupsLoaded = true;
    if (this.groups.length === 0) {
      this.selectedGroup = '';
      sessionStorage.removeItem('groupMgmtSelectedGroup');
    }
    this.refreshGroupDropdownState();
  }

  gridOptions = {
    domLayout: 'normal' as const,
  };

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    floatingFilter: true,
    sortingOrder: ['asc', 'desc'],
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
  };

  columnDefs: ColDef[] = [
    {
      headerName: 'Customer Name',
      field: 'customerName',
      flex: 1.2,
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      // Client-side sort only (BE does not support customerName sort)
      sortable: true,
      filterValueGetter: (params: any) => {
        const d = params?.data;
        const name = d?.customer?.customerName ?? d?.customerName ?? null;
        // Public groups default to Montage when BE omits customer on the group entity
        if (!name && d?.isCommon === true) return 'Montage';
        return name ?? 'N/A';
      },
      cellRenderer: (params: any) => {
        const d = params?.data;
        let name: string | null = d?.customer?.customerName ?? d?.customerName ?? null;
        const id: string | number | null = d?.configCustomerId ?? d?.customer?.id ?? d?.customerId ?? null;

        // Public groups default to Montage when BE does not return a customer
        if (!name && d?.isCommon === true) {
          name = 'Montage';
        }

        const nameHtml = name
          ? `<span style="font-weight:500;color:#1a1a1a;">${name}</span>`
          : '<span style="color:#aaa;">N/A</span>';

        const idHtml = id != null
          ? `<span style="font-size:11px;color:#888;margin-left:4px;">(${id})</span>`
          : '';

        return `${nameHtml}${idHtml}`;
      },
    },
    {
      headerName: 'Group Name',
      field: 'groupName',
      flex: 1.2,
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        const d = params?.data;
        const name: string = d?.groupName ?? 'N/A';
        const id: string | number | null = d?.configGroupId ?? d?.id ?? null;

        const nameHtml = `<span style="font-weight:500;color:#1a1a1a;">${name}</span>`;
        const idHtml = id != null
          ? `<span style="font-size:11px;color:#888;margin-left:4px;">(${id})</span>`
          : '';

        return `${nameHtml}${idHtml}`;
      },
    },
    {
      headerName: 'Group Type',
      field: 'isCommon',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      floatingFilterComponent: GroupTypeFloatingFilter,
      filterValueGetter: (params: any) => {
        if (params?.data?.isCommon === true) return 'Public';
        if (params?.data?.isCommon === false) return 'Private';
        return 'N/A';
      },
      cellRenderer: (params: any) => {
        if (params?.data?.isCommon === true) return 'Public';
        if (params?.data?.isCommon === false) return 'Private';
        return 'N/A';
      },
    },
    {
      headerName: 'Device Count',
      field: 'deviceCount',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => String((params?.data?.deviceCount) ?? 'N/A'),
    },
    {
      headerName: 'Created By',
      field: 'createdBy',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: TEXT_FILTER_PARAMS,
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => params?.data?.createdBy ?? 'N/A',
    },
    withDateColumnLayout({
      headerName: 'Created On (UTC)',
      field: 'createdOn',
      sort: 'desc',
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      filterParams: createUtcDateFilterParams(),
      cellRenderer: (params: any) => formatUtcDateTimeCell(params?.data?.createdOn),
    }),
    {
      headerName: 'Action',
      field: 'action',
      flex: 0.8,
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressHeaderMenuButton: true,
      cellRenderer: GroupActionCellRendererComponent,
      cellRendererParams: {
        onEdit: (data: any) => this.onEditGroup(data),
        onDelete: (data: any) => this.onDeleteGroup(data),
      },
    },
  ];

  private applyRbacToGrid(): void {
    if (!this.fullColumnDefs) {
      this.fullColumnDefs = [...this.columnDefs];
    }

    const canEdit = this.rbacService.hasPermission(RbacPermissions.UserMgmt_Group_Edit);
    const canDelete = this.rbacService.hasPermission(
      RbacPermissions.UserMgmt_Group_Delete
    );

    const nextDefs = this.fullColumnDefs.filter((col) => {
      if (col.colId === 'action' || col.field === 'action') {
        return canEdit || canDelete;
      }
      return true;
    });

    const defsChanged =
      nextDefs.length !== this.columnDefs.length ||
      nextDefs.some(
        (col, i) =>
          (col.colId ?? col.field) !==
          (this.columnDefs[i]?.colId ?? this.columnDefs[i]?.field)
      );

    this.columnDefs = nextDefs;

    if (this.gridApi && defsChanged) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
    }
  }

  private searchPayload = {
    page: 0,
    size: AG_GRID_DEFAULT_PAGE_SIZE,
    filters: [] as { field: string; operator: string; value: any }[],
    sorts: [{ field: 'createdOn', direction: 'desc' }],
  };

  dataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => {
      this.executeSearch(params);
    },
  };

  private executeSearch(params: IGetRowsParams): void {
    this.isLoading = true;
    this.sortForAgGrid(params.sortModel);
    this.applyFiltersToSearchPayload(params.filterModel ?? {});

    this.searchPayload.page = Math.floor(
      params.startRow / this.paginationPageSize
    );
    this.searchPayload.size = this.paginationPageSize;

    this.searchSub = this.groupService.searchGroups(this.searchPayload).subscribe({
      next: (res) => {
        const page = res?.data ?? res;
        let content = page?.content ?? res?.data?.content ?? res?.content ?? res ?? [];
        content = Array.isArray(content) ? content : [];
        content = this.applyClientSortIfNeeded(content, params.sortModel);

        this.rowData = content;
        this.totalElements = page?.totalElements ?? content.length;
        this.syncGridHostHeight(content.length);
        params.successCallback(this.rowData, this.totalElements);
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load groups:', err);
        this.rowData = [];
        this.totalElements = 0;
        this.syncGridHostHeight(0);
        params.failCallback();
        this.isLoading = false;
      },
    });
  }

  /**
   * Size host to the loaded page of rows (default fixed row height).
   * Full content height so the grid does not need an internal vertical scrollbar.
   */
  private syncGridHostHeight(loadedRowCount: number): void {
    const rows = Number.isFinite(loadedRowCount) && loadedRowCount > 0 ? loadedRowCount : 0;
    const bodyPx =
      rows > 0 ? rows * AG_GRID_DEFAULT_ROW_HEIGHT_PX : AG_GRID_EMPTY_BODY_ROW_PX;
    const contentPx = AG_GRID_HEADER_AND_PAGING_PX + bodyPx;
    const minHostPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
    this.gridHostHeightPx = Math.max(minHostPx, contentPx);
  }

  constructor(
    private dialog: MatDialog,
    private groupService: GroupService,
    private customerService: CustomerService,
    private deviceService: DeviceService,
    private toastr: ToastrService
  ) {}

  canDeactivate(): Promise<boolean> {
    return this.unsavedHost.canDeactivate();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent): void {
    this.unsavedHost.onBeforeUnload(event);
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.unsavedHost.clearOnDestroy();
  }

  ngOnInit(): void {
    this.applyRbacToGrid();
    this.rbacService.contextChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyRbacToGrid());
    this.rbacService.ensureContextLoaded().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    const storedCustomer = sessionStorage.getItem('groupMgmtSelectedCustomer');
    if (storedCustomer) {
      this.selectedCustomer = storedCustomer;
    }
    const storedGroup = sessionStorage.getItem('groupMgmtSelectedGroup');
    if (storedGroup) {
      this.selectedGroup = storedGroup;
    }

    // Restore persisted sort into payload and column defs before first load
    const storedSorting = sessionStorage.getItem(SESSION_SORT_KEY);
    if (storedSorting) {
      const sortState = JSON.parse(storedSorting);
      this.searchPayload.sorts[0].field = sortState.colId;
      this.searchPayload.sorts[0].direction = sortState.sort;
      this.columnDefs = this.columnDefs.map((col) => ({
        ...col,
        sort: (col.colId ?? col.field) === sortState.colId ? sortState.sort : null,
      }));
    }
    this.loadCustomersForDropdown();
  }

  private loadCustomersForDropdown(): void {
    this.customerService
      .getAllCustomer({
        page: 0,
        size: 500,
        filters: [{ field: '', operator: '', value: '' }],
        sorts: [{ field: 'customerName', direction: 'asc' }],
      })
      .subscribe((res) => {
        this.customerList = res?.data?.content ?? [];
        if (this.selectedCustomer && this.selectedCustomer !== 'All') {
          this.loadGroupsForCustomer(this.selectedCustomer);
        }
      });
  }

  private loadGroupsForCustomer(customerName: string): void {
    const selectedCustomerObj = this.customerList.find(
      (obj: any) => obj.customerName === customerName
    );
    if (!selectedCustomerObj?.id) {
      this.applyLoadedGroups([]);
      return;
    }
    this.groupsLoaded = false;
    this.refreshGroupDropdownState();
    this.deviceService.getOtagroupIdList(selectedCustomerObj.id).subscribe((response: any) => {
      this.applyLoadedGroups(response?.data?.content);
    });
  }

  onHeaderCustomerChange(event: Event): void {
    this.selectedCustomer = (event.target as HTMLSelectElement).value || 'All';
    sessionStorage.setItem('groupMgmtSelectedCustomer', this.selectedCustomer);

    this.groups = [];
    this.selectedGroup = '';
    sessionStorage.removeItem('groupMgmtSelectedGroup');

    if (this.selectedCustomer !== 'All') {
      this.groupsLoaded = false;
      this.refreshGroupDropdownState();
      this.loadGroupsForCustomer(this.selectedCustomer);
    } else {
      this.groupsLoaded = true;
      this.refreshGroupDropdownState();
    }

    this.applyHeaderFiltersToGridColumns({
      customerName: this.selectedCustomer === 'All' ? null : this.selectedCustomer,
      groupName: null,
    });
  }

  onHeaderGroupChange(event: Event): void {
    this.selectedGroup = (event.target as HTMLSelectElement).value || '';
    if (this.selectedGroup) {
      sessionStorage.setItem('groupMgmtSelectedGroup', this.selectedGroup);
    } else {
      sessionStorage.removeItem('groupMgmtSelectedGroup');
    }
    this.applyHeaderFiltersToGridColumns({
      groupName: this.selectedGroup || null,
    });
  }

  private applyHeaderFiltersToGridColumns(updates: {
    customerName?: string | null;
    groupName?: string | null;
  }): void {
    if (!this.gridApi) {
      return;
    }
    const model: Record<string, any> = { ...(this.gridApi.getFilterModel() || {}) };
    const textContains = (value: string) => ({
      filterType: 'text',
      type: 'contains',
      filter: value,
    });

    if (updates.customerName !== undefined) {
      if (updates.customerName) {
        model['customerName'] = textContains(updates.customerName);
      } else {
        delete model['customerName'];
      }
    }
    if (updates.groupName !== undefined) {
      if (updates.groupName) {
        model['groupName'] = textContains(updates.groupName);
      } else {
        delete model['groupName'];
      }
    }

    this.syncingHeaderColumnFilters = true;
    this.gridApi.setFilterModel(model);
    queueMicrotask(() => {
      this.syncingHeaderColumnFilters = false;
    });
  }

  onGridFilterChanged(): void {
    if (!this.gridApi || this.syncingHeaderColumnFilters) {
      return;
    }
    const model = this.gridApi.getFilterModel() || {};
    const customerFilter = String(model['customerName']?.filter ?? '').trim();
    const groupFilter = String(model['groupName']?.filter ?? '').trim();

    if (!customerFilter) {
      if (this.selectedCustomer !== 'All') {
        this.selectedCustomer = 'All';
        sessionStorage.setItem('groupMgmtSelectedCustomer', 'All');
        this.groups = [];
        this.selectedGroup = '';
        sessionStorage.removeItem('groupMgmtSelectedGroup');
        this.groupsLoaded = true;
        this.refreshGroupDropdownState();
      }
    } else {
      const exact = this.customerList.find((c: any) => c.customerName === customerFilter);
      if (exact && this.selectedCustomer !== exact.customerName) {
        this.selectedCustomer = exact.customerName;
        sessionStorage.setItem('groupMgmtSelectedCustomer', this.selectedCustomer);
        this.loadGroupsForCustomer(exact.customerName);
      }
    }

    if (!groupFilter) {
      if (this.selectedGroup) {
        this.selectedGroup = '';
        sessionStorage.removeItem('groupMgmtSelectedGroup');
      }
    } else {
      const exactGroup = this.groups.find((g: any) => g.groupName === groupFilter);
      if (exactGroup && this.selectedGroup !== exactGroup.groupName) {
        this.selectedGroup = exactGroup.groupName;
        sessionStorage.setItem('groupMgmtSelectedGroup', this.selectedGroup);
      } else if (!exactGroup && this.groups.length === 0 && this.selectedCustomer !== 'All') {
        this.selectedGroup = groupFilter;
        sessionStorage.setItem('groupMgmtSelectedGroup', this.selectedGroup);
      }
    }
  }

  private refreshGroupsGrid(): void {
    if (!this.gridApi) {
      return;
    }
    this.searchPayload.page = 0;
    this.gridApi.purgeInfiniteCache();
  }

  private applyFiltersToSearchPayload(filterModel: Record<string, any>): void {
    const nextFilters: { field: string; operator: string; value: any }[] = [];

    if (filterModel && Object.keys(filterModel).length > 0) {
      Object.keys(filterModel).forEach((key) => {
        const filter = filterModel[key];
        if (!filter) {
          return;
        }

        if (key === 'isCommon') {
          const val = String(filter.filter ?? '').trim();
          if (val === 'Public') {
            nextFilters.push({ field: 'isCommon', operator: 'eq', value: true });
          } else if (val === 'Private') {
            nextFilters.push({ field: 'isCommon', operator: 'eq', value: false });
          }
          return;
        }

        const mapped = mapAgGridFilterToApi(key, filter);
        if (mapped) {
          nextFilters.push({
            field: mapped.field,
            operator: mapped.operator,
            value: mapped.value,
          });
        }
      });
    }

    this.searchPayload.filters = nextFilters;
  }

/* Start Copy Data cell Value function   */
contextMenu: { visible: boolean; x: number; y: number } = {
  visible: false,
  x: 0,
  y: 0
};
selectedCellValue: string = '';
contextMenuVisible: boolean = false;
contextMenuPosition = { x: 0, y: 0 };

getNestedProperty(obj: any, path: string): any {
  return get(obj, path, 'N/A'); // 'N/A' if value is undefined
}

onCellContextMenu(event: any) {
  event.event.preventDefault();
  const columnField = event.colDef.field;
  let cellValue = 'N/A';
  if (event.colDef.cellRenderer) {
    const cellRenderer = event.colDef.cellRenderer;
    const rendererParams = { data: event.data, colDef: event.colDef, value: event.value };
    const renderedValue = cellRenderer(rendererParams);
    if (typeof renderedValue === 'string') {
      cellValue = renderedValue.replace(/<[^>]*>/g, '');
    } else if (renderedValue?.outerText) {
      cellValue = renderedValue.outerText;
    }
  } else {
    cellValue = this.getNestedProperty(event.data, columnField);
  }

  // Check if the data and field are available
  if (event.node && event.node.data) {
    this.selectedCellValue = cellValue;
    this.contextMenuPosition = {
      x: event.event.clientX,
      y: event.event.clientY
    };
    this.contextMenuVisible = true;
  } else {
    this.contextMenuVisible = false;
  }
}

handleCopy() {
  navigator.clipboard.writeText(this.selectedCellValue)
  .then(() => {
    this.contextMenuVisible = false;
  })
  .catch(err => console.error('Failed to copy:', err));
}

// Hide context menu on outside click
@HostListener('document:click')
onDocumentClick(): void {
  this.contextMenuVisible = false;
}
preventDefaultContextMenu(event: MouseEvent): void {
  event.preventDefault();
}
/* End Copy Data cell Value function   */

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    const customerName =
      this.selectedCustomer && this.selectedCustomer !== 'All'
        ? this.selectedCustomer
        : null;
    const groupName = this.selectedGroup || null;
    if (customerName || groupName) {
      this.applyHeaderFiltersToGridColumns({ customerName, groupName });
    }
    this.gridApi.setGridOption('datasource', this.dataSource);
  }

  onPaginationChanged(event: PaginationChangedEvent): void {
    const newPageSize = event.api.paginationGetPageSize();
    if (
      Number.isFinite(newPageSize) &&
      newPageSize > 0 &&
      newPageSize !== this.paginationPageSize
    ) {
      this.paginationPageSize = newPageSize;
      this.searchPayload.size = newPageSize;
      this.searchPayload.page = 0;
    }
    this.currentPage = event.api.paginationGetCurrentPage();
  }

  private sortForAgGrid(
    sortModel: { colId: string; sort: 'asc' | 'desc' | null | undefined }[]
  ): void {
    if (!sortModel.length || !sortModel[0]?.colId) {
      return;
    }

    const { colId, sort } = sortModel[0];
    if (!sort) {
      return;
    }

    const serverSortableFields: Record<string, string> = {
      groupName: 'groupName',
      isCommon: 'isCommon',
      createdOn: 'createdOn',
    };

    sessionStorage.setItem(SESSION_SORT_KEY, JSON.stringify(sortModel[0]));
    this.sortFlag = true;

    if (serverSortableFields[colId]) {
      this.searchPayload.sorts[0].field = serverSortableFields[colId];
      this.searchPayload.sorts[0].direction = sort;
    }
  }

  private applyClientSortIfNeeded(
    rows: any[],
    sortModel: { colId: string; sort: 'asc' | 'desc' | null | undefined }[]
  ): any[] {
    if (!sortModel.length || !sortModel[0]?.colId || !sortModel[0]?.sort) {
      return rows;
    }

    const { colId, sort } = sortModel[0];
    const clientSortFields: Record<string, (item: any) => any> = {
      deviceCount: (item) => item?.deviceCount ?? null,
      createdBy: (item) => item?.createdBy ?? null,
      customerName: (item) =>
        item?.customer?.customerName ??
        item?.customerName ??
        (item?.isCommon === true ? 'Montage' : null),
    };

    const getValue = clientSortFields[colId];
    if (!getValue) {
      return rows;
    }

    const dir = sort === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * dir;
      }
      return String(aVal).localeCompare(String(bVal)) * dir;
    });
  }

  onAddGroup(): void {
    const dialogRef = this.dialog.open(GroupDialogComponent, {
      width: '600px',
      restoreFocus: false,
      closeOnNavigation: false,
      data: null,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.refreshGroupsGrid();
      }
    });
  }

  onEditGroup(data: any): void {
    const deviceCount = Number(data?.deviceCount);
    const dialogData: GroupDialogData = {
      id: data.id,
      groupName: data.groupName,
      groupType: data.isCommon ? 'COMMON' : 'CUSTOMER_SPECIFIC',
      customerId: data.customerId ?? data.customer?.id,
      customerName: data.customerName ?? data.customer?.customerName,
      deviceCount: Number.isFinite(deviceCount) ? deviceCount : 0,
    };
    const dialogRef = this.dialog.open(GroupDialogComponent, {
      width: '600px',
      restoreFocus: false,
      closeOnNavigation: false,
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.refreshGroupsGrid();
      }
    });
  }

  onDeleteGroup(data: any): void {
    const deviceCount = Number(data?.deviceCount);
    if (Number.isFinite(deviceCount) && deviceCount > 0) {
      this.toastr.warning(
        '<b>Warning</b>: All devices must be removed from this group or moved to another group before deleting.',
        '',
        { timeOut: 8000, closeButton: true, progressBar: true, enableHtml: true }
      );
      return;
    }

    const groupType = data?.isCommon ? 'Public' : 'Private';

    const dialogRef = this.dialog.open(ConfirmDeleteGroupComponent, {
      width: '520px',
      restoreFocus: false,
      data: {
        groupName: data?.groupName ?? 'N/A',
        groupType,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.groupService.deleteGroup(data.id).subscribe({
        next: (response: any) => {
          this.toastr.success(
            response?.message ?? `Group "${data?.groupName}" has been deleted successfully.`,
            'Group Deleted',
            { timeOut: 30000, closeButton: true, progressBar: true }
          );
          this.refreshGroupsGrid();
        },
        error: (error: any) => {
          const msg =
            error?.error?.message ??
            error?.message ??
            `Failed to delete group "${data?.groupName}". Please try again.`;
          this.toastr.error(msg, 'Delete Failed', {
            timeOut: 8000,
            closeButton: true,
            progressBar: true,
          });
        },
      });
    });
  }
}
